import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { SelectPropsValue } from 'commons/components';
import { useDeviceTypesContext } from 'core/contexts/device-types';
import { useTranslation, TFunction } from 'core/i18n/i18n';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import {
    DeviceTypeDetails,
    DeviceTypeOperatorListOptions,
    DeviceTypeOperatorListOptionsCodes,
} from 'modules/devices/domain/api/device-type.api';
import {
    SettingsLoadingContainer,
    SettingsViewContent,
    SettingsViewHeader,
    SettingsViewLayout,
} from 'modules/settings/settings.style';
import { useUpdateDeviceType } from './service/use-update-device-type.hook';
import { DeviceCardComponent } from './commons/device-card.component';

const getDeviceTypeOperatorListModeTranslation = (mode: string, t: TFunction) =>
    t(`settings.devices.device-types.device.operators-list.${mode}`);

const deviceTypeOperatorsListOptions = (t: TFunction): { value: DeviceTypeOperatorListOptions; text: string }[] =>
    Object.keys(DeviceTypeOperatorListOptionsCodes).map((k) => ({
        value: k as DeviceTypeOperatorListOptions,
        text: getDeviceTypeOperatorListModeTranslation(k, t),
    }));

export const SettingsDeviceTypesContainer: React.FC = () => {
    const { deviceTypes, setDeviceTypes, isLoading } = useDeviceTypesContext();
    const { updateDeviceType } = useUpdateDeviceType();

    const { t } = useTranslation();

    const handleChangeDeviceType = (deviceType: DeviceTypeDetails, value: SelectPropsValue) => {
        const deviceTypesCopy = [...deviceTypes];
        const deviceTypeSelected = deviceTypes.find((dt) => deviceType.id === dt.id);
        if (deviceTypeSelected) {
            const revertValue = deviceTypeSelected.operatorListMode;
            deviceTypeSelected.operatorListMode = value![0] as DeviceTypeOperatorListOptions;
            updateDeviceType(deviceTypeSelected)
                .then(() => {
                    showSuccessNotification(
                        t('settings.devices.device-types.device.operations.modify.notification.positive', {
                            name: deviceType.name,
                        })
                    );
                })
                .catch(() => {
                    deviceTypeSelected.operatorListMode = revertValue;
                    setDeviceTypes(deviceTypesCopy);
                    showErrorNotification(
                        t('settings.devices.device-types.device.operations.modify.notification.negative', {
                            name: deviceType.name,
                        })
                    );
                });
        }
    };

    return (
        <SettingsViewLayout>
            <SettingsViewHeader>
                <PocTypography element="h5" variant="title5">
                    {t('settings.devices.device-types.title')}
                </PocTypography>
            </SettingsViewHeader>
            <SettingsViewContent>
                {isLoading ? (
                    <SettingsLoadingContainer>
                        <PocProgressSpinner variant="primary" size="large" />
                    </SettingsLoadingContainer>
                ) : (
                    <>
                        {deviceTypes.length ? (
                            deviceTypes.map((deviceType) => (
                                <DeviceCardComponent
                                    key={deviceType.id}
                                    deviceTypeDetails={deviceType}
                                    options={deviceTypeOperatorsListOptions(t)}
                                    onChangeOperatorsListOption={(_field, value) =>
                                        handleChangeDeviceType(deviceType, value)
                                    }
                                />
                            ))
                        ) : (
                            <PocTypography element="p" variant="body1">
                                {t('settings.devices.device-types.device.empty')}
                            </PocTypography>
                        )}
                    </>
                )}
            </SettingsViewContent>
        </SettingsViewLayout>
    );
};
